import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAutosave } from "@/lib/useAutosave";
import { useDraftBackup, DraftRecoverBanner } from "@/components/app/DraftRecover";
import { FileText, X, Save, Loader2, Palette } from "lucide-react";
import { PLAN_THEMES } from "@/components/app/PlaneacionFormato";
import RichText from "@/components/app/RichText";
import FirmasEditor from "@/components/app/FirmasEditor";

const DAYS = [
  ["monday", "Monday"], ["tuesday", "Tuesday"], ["wednesday", "Wednesday"],
  ["thursday", "Thursday"], ["friday", "Friday"],
];
const DAY_COLS = [
  ["warm_up", "Warm up", 2], ["activities", "Activities", 6], ["sources", "Sources", 2],
  ["evaluation", "Evaluation", 2], ["homework", "Homework", 2],
];

// Input de una sola línea (identidad estable a nivel de módulo para no perder el foco al escribir).
const TextInput = ({ label, value, onChange, testid, color }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">{label}</label>
    <input value={value} onChange={onChange} data-testid={testid} style={{ "--tw-ring-color": color }}
      className="mt-1 w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm text-navy outline-none focus:ring-2" />
  </div>
);

export default function PlaneacionIngles({ initial, defaultDocente, onSaved, onCancel }) {
  const [titulo, setTitulo] = useState(initial?.proyecto || "");
  const [color, setColor] = useState(initial?.color || "#5f9f46");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(initial?.id || null);
  const [d, setD] = useState(() => ({
    title: initial?.data?.title || "",
    subtitle: initial?.data?.subtitle || "",
    teacher_name: initial?.data?.teacher_name || defaultDocente || "",
    group: initial?.data?.group || "",
    date: initial?.data?.date || "",
    topic: initial?.data?.topic || "",
    objective: initial?.data?.objective || "",
    contenido: initial?.data?.contenido || "",
    pda: initial?.data?.pda || "",
    eje_articulador: initial?.data?.eje_articulador || "",
    campo_formativo: initial?.data?.campo_formativo || "",
    days: initial?.data?.days || {},
    pm_date: initial?.data?.pm_date || "",
    pm_activity: initial?.data?.pm_activity || "",
    pm_development: initial?.data?.pm_development || "",
    pm_evidence: initial?.data?.pm_evidence || "",
    sig_left_label: initial?.data?.sig_left_label || "",
    sig_right_label: initial?.data?.sig_right_label || "",
    firma_docente_name: initial?.data?.firma_docente_name || "",
    firma_docente_rol: initial?.data?.firma_docente_rol || "Docente",
    firma_director_name: initial?.data?.firma_director_name || "",
    firma_director_rol: initial?.data?.firma_director_rol || "Director(a)",
    firma_coord_name: initial?.data?.firma_coord_name || "",
    firma_coord_rol: initial?.data?.firma_coord_rol || "Coordinador(a)",
    _files: initial?.data?._files || {},
  }));

  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));
  const setDay = (day, k) => (e) => setD((s) => ({ ...s, days: { ...s.days, [day]: { ...(s.days[day] || {}), [k]: e.target.value } } }));
  const setFiles = (k) => (arr) => setD((s) => ({ ...s, _files: { ...(s._files || {}), [k]: arr } }));
  const files = (k) => (d._files || {})[k] || [];

  const savedSnap = useRef("");
  const savingRef = useRef(false);
  const draft = useDraftBackup({ tipo: "ingles", initialId: initial?.id });
  const applyDraft = (p) => { if (!p) return; setTitulo(p.titulo || ""); setColor(p.color || "#5f9f46"); if (p.d) setD(p.d); };
  const persist = async (silent = false) => {
    const body = { proyecto: titulo.trim() || "Planeación sin título", tipo: "ingles", color, formato: 4, data: d };
    const snap = JSON.stringify(body);
    if (silent && snap === savedSnap.current) return;
    if (savingRef.current) return;
    savingRef.current = true;
    if (!silent) setSaving(true);
    try {
      if (savedId) await api.put(`/docente/planeaciones/${savedId}`, body);
      else { const { data: res } = await api.post("/docente/planeaciones", body); setSavedId(res.id); }
      savedSnap.current = snap;
      draft.clear();
      if (!silent) onSaved();
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { savingRef.current = false; if (!silent) setSaving(false); }
  };
  const save = () => persist(false);
  const baseWatch = useRef();
  const watch = JSON.stringify(d) + "|" + titulo + "|" + color;
  if (baseWatch.current === undefined) baseWatch.current = watch;
  useAutosave(watch, () => { draft.backup({ titulo, color, d }); persist(true); }, { enabled: watch !== baseWatch.current });

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel} data-testid="pi-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full my-8 max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl text-white" style={{ background: color }}>
          <div className="font-display font-bold flex items-center gap-2"><FileText className="w-4 h-4" /> {initial?.id ? "Editar" : "Nueva"} planeación · Inglés</div>
          <button onClick={onCancel} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-5">
          <DraftRecoverBanner pending={draft.pending} onRecover={() => { applyDraft(draft.pending); draft.consumed(); }} onDismiss={draft.dismiss} />
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Nombre de la planeación (para tu lista)" data-testid="pi-titulo"
            className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy font-semibold mb-3" />

          <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-nardo-500"><Palette className="w-3 h-3" /> Color</div>
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="pi-formatos">
            {PLAN_THEMES.map((t) => (
              <button key={t.n} type="button" onClick={() => setColor(t.color)} title={t.name}
                className={`w-7 h-7 rounded-full border-2 transition-transform ${color === t.color ? "scale-110 border-navy" : "border-white"}`}
                style={{ background: t.color }} data-testid={`pi-color-${t.n}`} />
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-4 p-3 rounded-lg border border-dashed border-nardo-300">
            <div className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Encabezado del formato (universal · editable)</div>
            <TextInput label="Título" value={d.title} onChange={set("title")} testid="pi-title" color={color} />
            <TextInput label="Subtítulo (opcional)" value={d.subtitle} onChange={set("subtitle")} testid="pi-subtitle" color={color} />
            <p className="sm:col-span-2 text-[10px] text-nardo-400">Si dejas el título vacío se usa “WEEKLY LESSON PLAN — ENGLISH”. Este formato no lleva logo ni marca de agua ni nombre de colegio.</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <TextInput label="Teacher's name" value={d.teacher_name} onChange={set("teacher_name")} testid="pi-teacher_name" color={color} />
            <TextInput label="Group" value={d.group} onChange={set("group")} testid="pi-group" color={color} />
            <TextInput label="Date" value={d.date} onChange={set("date")} testid="pi-date" color={color} />
            <TextInput label="Topic" value={d.topic} onChange={set("topic")} testid="pi-topic" color={color} />
          </div>

          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">Objective</label>
            <RichText value={d.objective} onChange={set("objective")} testid="pi-objective" rows={2} color={color}
              files={files("objective")} onFilesChange={setFiles("objective")} label="Objective" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 p-3 rounded-lg" style={{ background: `${color}12` }}>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">Contenido</label>
              <RichText value={d.contenido} onChange={set("contenido")} testid="pi-contenido" rows={2} color={color}
                files={files("contenido")} onFilesChange={setFiles("contenido")} label="Contenido" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">PDA</label>
              <RichText value={d.pda} onChange={set("pda")} testid="pi-pda" rows={2} color={color}
                files={files("pda")} onFilesChange={setFiles("pda")} label="PDA" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">Eje articulador</label>
              <RichText value={d.eje_articulador} onChange={set("eje_articulador")} testid="pi-eje_articulador" rows={2} color={color}
                files={files("eje_articulador")} onFilesChange={setFiles("eje_articulador")} label="Eje articulador" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">Campo formativo</label>
              <RichText value={d.campo_formativo} onChange={set("campo_formativo")} testid="pi-campo_formativo" rows={2} color={color}
                files={files("campo_formativo")} onFilesChange={setFiles("campo_formativo")} label="Campo formativo" />
            </div>
          </div>

          <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Actividades por día (lunes a viernes)</div>
          <div className="space-y-4">
            {DAYS.map(([dk, dl]) => (
              <div key={dk} className="border border-nardo-200 rounded-xl overflow-hidden" data-testid={`pi-day-${dk}`}>
                <div className="px-3 py-2 text-white text-sm font-bold" style={{ background: color }}>{dl}</div>
                <div className="p-3 space-y-3">
                  {DAY_COLS.map(([ck, cl, rws]) => {
                    const fk = `day_${dk}_${ck}`;
                    return (
                      <div key={ck} className="w-full">
                        <label className="text-[10px] uppercase tracking-wide text-nardo-500 font-semibold">{cl}</label>
                        <RichText value={(d.days[dk] || {})[ck] || ""} onChange={setDay(dk, ck)} testid={`pi-${dk}-${ck}`} rows={rws} minRows={rws} color={color}
                          files={files(fk)} onFilesChange={setFiles(fk)} label={`${dl} · ${cl}`}
                          placeholder={ck === "activities" ? "Describe con detalle la actividad del día… (el recuadro crece conforme escribes)" : ""} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-nardo-400 mt-2">Cada recuadro es a lo ancho y crece automáticamente conforme escribes, para que nada se encime.</p>

          <div className="mt-5 mb-2 text-[11px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Preventive measures to safeguard the integrity of students</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4 p-3 rounded-lg border border-nardo-200">
            <div>
              <label className="text-[9px] uppercase tracking-wide text-nardo-400 font-semibold">Date</label>
              <RichText value={d.pm_date} onChange={set("pm_date")} testid="pi-pm_date" rows={2} color={color} files={files("pm_date")} onFilesChange={setFiles("pm_date")} label="PM · Date" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-wide text-nardo-400 font-semibold">Activity</label>
              <RichText value={d.pm_activity} onChange={set("pm_activity")} testid="pi-pm_activity" rows={2} color={color} files={files("pm_activity")} onFilesChange={setFiles("pm_activity")} label="PM · Activity" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-wide text-nardo-400 font-semibold">Development</label>
              <RichText value={d.pm_development} onChange={set("pm_development")} testid="pi-pm_development" rows={2} color={color} files={files("pm_development")} onFilesChange={setFiles("pm_development")} label="PM · Development" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-wide text-nardo-400 font-semibold">Evidence</label>
              <RichText value={d.pm_evidence} onChange={set("pm_evidence")} testid="pi-pm_evidence" rows={2} color={color} files={files("pm_evidence")} onFilesChange={setFiles("pm_evidence")} label="PM · Evidence" />
            </div>
          </div>

          <div className="mb-2">
            <FirmasEditor d={d} set={set} base="pi-firmas" color={color} />
          </div>

          <p className="text-[11px] text-nardo-400 mt-3">Esta planeación se genera sin logo. Las firmas escaneadas del maestro y de coordinación de inglés se insertan automáticamente si están cargadas; el nombre que escribas arriba aparece encima del rol. Puedes adjuntar archivos en los recuadros grandes.</p>

          <div className="flex justify-end gap-2 mt-4">
            <span className="mr-auto self-center text-[11px] text-nardo-400" data-testid="pi-autosave-hint">Autoguardado activo</span>
            <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-nardo-200 text-sm text-nardo-600 hover:bg-nardo-50" data-testid="pi-cancel">Cerrar</button>
            <button onClick={save} disabled={saving} data-testid="pi-save" className="px-4 py-2 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" style={{ background: color }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios y contenido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
